import {
	DEFAULT_TIME_ZONE,
	isWaitUntil,
	isWaitWeekday,
	type WaitUnit,
	WEEKDAY_LABELS,
} from "@velizhanin-dev/bot-scenario"
import { DateField, TimeField } from "@/shared/ui/date-time-field"
import { Field } from "@/shared/ui/form-field"
import { NativeSelect } from "@/shared/ui/native-select"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { DurationInputs } from "../duration-inputs"
import type { EditorProps } from "../editor-props"
import { readNumber, readString, withField } from "../editor-props"
import { issuesAt } from "../issues-text"
import { LoadSwap } from "@/shared/ui/load-swap"

const WAIT_MODES = [
	{ value: "duration", label: "Через время" },
	{ value: "until", label: "До даты и времени" },
	{ value: "weekday", label: "До дня недели" },
] as const

const TIME_ZONE_OPTIONS = [
	{ value: DEFAULT_TIME_ZONE, label: "Москва" },
	{ value: "Europe/Kaliningrad", label: "Калининград" },
	{ value: "Europe/Samara", label: "Самара" },
	{ value: "Asia/Yekaterinburg", label: "Екатеринбург" },
	{ value: "Asia/Novosibirsk", label: "Новосибирск" },
	{ value: "Asia/Vladivostok", label: "Владивосток" },
	{ value: "Asia/Almaty", label: "Алматы" },
	{ value: "UTC", label: "UTC" },
]

export function WaitEditor({ config, onChange, issues }: EditorProps) {
	const until = isWaitUntil(config)
	const mode = until ? "until" : isWaitWeekday(config) ? "weekday" : "duration"
	const setMode = (next: "duration" | "until" | "weekday") => {
		if (next === mode) return
		onChange(
			next === "until"
				? { mode: "until", date: "", time: "10:00", timeZone: DEFAULT_TIME_ZONE }
				: next === "weekday"
					? { mode: "weekday", weekday: 1, time: "12:00", timeZone: DEFAULT_TIME_ZONE }
					: { amount: 1, unit: "hours" },
		)
	}
	const timeZone = readString(config, "timeZone") || DEFAULT_TIME_ZONE
	const timeZoneSelect = (
		<NativeSelect
			aria-label="Часовой пояс"
			value={timeZone}
			onChange={(event) => onChange({ ...config, timeZone: event.target.value })}
		>
			{TIME_ZONE_OPTIONS.some((option) => option.value === timeZone) ? null : (
				<option value={timeZone}>{timeZone}</option>
			)}
			{TIME_ZONE_OPTIONS.map((option) => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</NativeSelect>
	)
	return (
		<div className="space-y-4">
			<SegmentedControl
				aria-label="Как ждать"
				options={WAIT_MODES}
				value={mode}
				onChange={(value) => value && setMode(value)}
			/>
			<LoadSwap loading={false} id={mode === "weekday" ? "weekday" : until ? "until" : "pause"}>
				{mode === "weekday" ? (
					<Field
						label="Продолжить в ближайший"
						issues={[
							...issuesAt(issues, "weekday"),
							...issuesAt(issues, "time"),
							...issuesAt(issues, "timeZone"),
						]}
						hint="Например, ближайший вторник в 12:00. Если сегодня как раз этот день и время ещё не наступило, то сегодня."
					>
						<div className="grid grid-cols-2 gap-2">
							<NativeSelect
								aria-label="День недели"
								value={String(readNumber(config, "weekday") || 1)}
								onChange={(event) => onChange({ ...config, weekday: Number(event.target.value) })}
							>
								{Object.entries(WEEKDAY_LABELS).map(([day, label]) => (
									<option key={day} value={day}>
										{label.name}
									</option>
								))}
							</NativeSelect>
							<TimeField
								aria-label="Время"
								value={readString(config, "time")}
								onChange={(time) => onChange({ ...config, time })}
							/>
						</div>
						{timeZoneSelect}
					</Field>
				) : until ? (
					<Field
						label="Продолжить в"
						issues={[
							...issuesAt(issues, "date"),
							...issuesAt(issues, "time"),
							...issuesAt(issues, "timeZone"),
						]}
						hint="Если к моменту блока время уже прошло, бот пойдёт по выходу «Время уже прошло», например, к записи эфира."
					>
						<div className="grid grid-cols-2 gap-2">
							<DateField
								aria-label="Дата"
								value={readString(config, "date")}
								onChange={(date) => onChange({ ...config, date })}
							/>
							<TimeField
								aria-label="Время"
								value={readString(config, "time")}
								onChange={(time) => onChange({ ...config, time })}
							/>
						</div>
						<NativeSelect
							aria-label="Часовой пояс"
							value={timeZone}
							onChange={(event) => onChange({ ...config, timeZone: event.target.value })}
						>
							{TIME_ZONE_OPTIONS.some((option) => option.value === timeZone) ? null : (
								<option value={timeZone}>{timeZone}</option>
							)}
							{TIME_ZONE_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</NativeSelect>
					</Field>
				) : (
					<Field
						label="Продолжить через"
						issues={issuesAt(issues, "amount")}
						hint="Если пользователь напишет во время паузы, ответит воронка «Не понял», и пауза продолжится."
					>
						<DurationInputs
							label="Пауза"
							value={{
								amount: readNumber(config, "amount"),
								unit: (readString(config, "unit") || "hours") as WaitUnit,
							}}
							onChange={(next) =>
								onChange({
									...withField(config, "amount", next.amount),
									unit: next.unit ?? "hours",
								})
							}
						/>
					</Field>
				)}
			</LoadSwap>
		</div>
	)
}
